import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../component/Spinner";
import axiosInstance from "../networking/axiosconfig";
import Navbar from "./Navbar";
import { Settings, Edit, Calendar, CheckCircle2, ShieldCheck, Clock, Monitor, ArrowUpRight, Cpu } from "lucide-react";
import ChatbotPopup from "../component/chatbotPopup";
import { POLICY_OPTIONS } from "../utils/weekendPolicy";

const GeneralSettingList = () => {
  const navigate = useNavigate();
  const [settingList, setSettingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showChatbotPopup, setShowChatbotPopup] = useState(false);

  // 🏢 SaaS Weekend / Weekly Off Policy State
  const [weekendPolicy, setWeekendPolicy] = useState("LAST_SATURDAY_OFF");
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyMessage, setPolicyMessage] = useState({ text: "", type: "" });

  const genralSettingUrl = "/api/setting/";

  /* ================= FETCH WEEKEND POLICY ================= */
  const fetchWeekendPolicy = async () => {
    try {
      setLoadingPolicy(true);
      const res = await axiosInstance.get("/api/organization/weekend-policy");
      if (res.data?.status && res.data.data?.weekendPolicy) {
        setWeekendPolicy(res.data.data.weekendPolicy);
      }
    } catch (err) {
      console.error("Failed to fetch weekend policy:", err);
    } finally {
      setLoadingPolicy(false);
    }
  };

  /* ================= SAVE WEEKEND POLICY ================= */
  const handleSaveWeekendPolicy = async () => {
    try {
      setSavingPolicy(true);
      setPolicyMessage({ text: "", type: "" });
      const res = await axiosInstance.post("/api/organization/weekend-policy", {
        weekendPolicy
      });
      if (res.data?.status) {
        setPolicyMessage({
          text: "Weekly off policy updated successfully! Working days and payroll rules have been re-calibrated.",
          type: "success"
        });
        setTimeout(() => setPolicyMessage({ text: "", type: "" }), 5000);
      }
    } catch (err) {
      console.error("Failed to update weekend policy:", err);
      setPolicyMessage({
        text: err.response?.data?.message || "Failed to update weekly off policy.",
        type: "error"
      });
    } finally {
      setSavingPolicy(false);
    }
  };

  /* ================= FETCH SETTING LIST ================= */
  const fetchSettingList = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(genralSettingUrl);
      if (res.data && Array.isArray(res.data.data)) {
        setSettingList(res.data.data);
      } else {
        console.error("Invalid data format");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ================= LOAD ON PAGE ================= */
  useEffect(() => {
    fetchSettingList();
    fetchWeekendPolicy();
  }, []);

  /* ================= EDIT ================= */
  const handleEditClick = (id) => {
    setSelectedId(id);
    setShowChatbotPopup(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />

      {/* Popup */}
      {showChatbotPopup && (
        <ChatbotPopup
          selectedId={selectedId}
          onClose={() => {
            setShowChatbotPopup(false);
            fetchSettingList(); // ✅ refresh after update
          }}
        />
      )}

      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-2xl shadow-2xl p-8 mb-6 text-white">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-lg">
              <Settings size={32} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">
                General Setting List
              </h1>
              <p className="text-xs md:text-sm ml-1">
                Welcome to the General Setting List.
              </p>
            </div>
          </div>
        </div>

        {/* 📟 BIOMETRIC MACHINE CLOUD COMMAND CENTER LAUNCH CARD */}
        <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 rounded-2xl shadow-xl p-6 sm:p-7 mb-6 text-white border border-blue-800 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 border border-white/20">
              <Monitor className="w-6 h-6 text-blue-300" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-bold text-white">
                  Biometric Machine Cloud Settings
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  Zero-Touch Remote Control
                </span>
              </div>
              <p className="text-xs text-blue-100/80 max-w-2xl leading-relaxed">
                Configure your physical T700 / T800 biometric hardware from anywhere: Sync machine clock with cloud time, update IP & port, change device name, control door locks, and restart devices without touching machine buttons.
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate('/biometric-settings')}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-lg transition-all whitespace-nowrap border border-blue-400/30 cursor-pointer"
          >
            <Cpu className="w-4 h-4" />
            Open Machine Settings
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>

        {/* 🏢 SAAS WORK WEEK & WEEKLY OFF POLICY CARD */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-6 border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-gray-100">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600">
                  <Calendar size={22} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">
                  Work Week & Weekly Off Policy
                </h2>
                <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  Automated Engine
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Define the recurring weekly off rule for your organization. Working days, calendar badges, and payroll deductions are dynamically calculated.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSaveWeekendPolicy}
                disabled={savingPolicy || loadingPolicy}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold rounded-xl text-xs shadow-md transition-all flex items-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {savingPolicy ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving Rule...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} />
                    Save Weekend Policy
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Policy Alert Feedback */}
          {policyMessage.text && (
            <div
              className={`mt-4 p-3 rounded-xl text-xs font-semibold flex items-center gap-2 ${
                policyMessage.type === "success"
                  ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                  : "bg-rose-50 text-rose-800 border border-rose-200"
              }`}
            >
              <CheckCircle2 size={16} />
              {policyMessage.text}
            </div>
          )}

          {/* Form Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                Select Saturday & Weekend Off Rule
              </label>
              <select
                value={weekendPolicy}
                onChange={(e) => setWeekendPolicy(e.target.value)}
                disabled={loadingPolicy || savingPolicy}
                className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-sm font-semibold text-gray-800 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all cursor-pointer"
              >
                {POLICY_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-gray-500 italic">
                {POLICY_OPTIONS.find((o) => o.key === weekendPolicy)?.description}
              </p>
            </div>

            {/* Quick Policy Summary Card */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50/60 p-4 rounded-xl border border-indigo-100 flex flex-col justify-between">
              <div>
                <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider mb-1">
                  Active Rule Applied
                </p>
                <p className="text-sm font-extrabold text-gray-800">
                  {POLICY_OPTIONS.find((o) => o.key === weekendPolicy)?.label || weekendPolicy}
                </p>
              </div>
              <div className="mt-3 pt-3 border-t border-indigo-100/60 flex items-center gap-2 text-[11px] text-gray-600">
                <Clock size={14} className="text-indigo-600" />
                <span>Zero manual holiday entries required</span>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="w-full">
          <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden mt-6">
            <thead className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white">
              <tr>
                <th className="py-3 px-4 text-left text-sm font-semibold text-white">S.No.</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-white">Title</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-white">Key</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-white">Value</th>
                <th className="py-3 px-4 text-center text-sm font-semibold text-white">
                  Action
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-12">
                    <Spinner size={30} color="#3498db" loading />
                  </td>
                </tr>
              ) : settingList.length > 0 ? (
                settingList.map((item, index) => (
                  <tr
                    key={item.setting_id_PK}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-4 text-sm">{index + 1}</td>
                    <td className="px-4 py-4 text-sm">{item.title}</td>
                    <td className="px-4 py-4 text-sm">{item.key}</td>
                    <td className="px-4 py-4 text-sm truncate max-w-xs">
                      {item.value}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button
                        onClick={() => handleEditClick(item.setting_id_PK)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-xs font-semibold border border-purple-200"
                      >
                        <Edit size={12} />
                        Edit ChatBot
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-6 text-gray-500">
                    No settings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettingList;
